import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../util/designUtil';

// Use environment variable with fallback
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const stripePromise = loadStripe(STRIPE_KEY);

// Get API URL from environment variable with fallback
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

console.log('Environment Variables:', {
  stripeKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
  apiUrl: API_URL,
  nodeEnv: process.env.NODE_ENV,
});

// Button component (minimal implementation for structure)
// If you have an actual Button component from a UI library, prefer using it.
const Button: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & {variant?: 'default' | 'outline' | 'ghost', size?: 'icon' | 'default'}> = ({ children, className, variant, size, ...props }) => {
  const baseStyle = "font-medium rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2";
  let variantStyle = "";
  if (variant === 'ghost') {
    variantStyle = "hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50";
    if (size === 'icon') variantStyle += " p-2"; else variantStyle += " px-4 py-2";
  } else if (variant === 'outline') {
    variantStyle = "border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 text-sm";
  } else { // default
    variantStyle = "bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:opacity-90 px-4 py-2 text-sm disabled:opacity-70";
  }
  return <button className={cn(baseStyle, variantStyle, className)} {...props}>{children}</button>;
};

console.log('Stripe Promise:', stripePromise);

interface PaymentFormProps {
  amount: number;
  onSuccess: (payementId : string) => void;
  onError: (error: string, payementId ?: string) => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ amount, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const { user } = useAuth();
  

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      onError('Stripe is not initialized');
      return;
    }
    
    setIsProcessing(true);
    setCardError(null);
    let paymentRef: any;

    try {
      const response = await fetch(`${API_URL}/api/payments/create-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount }),
      });

      if (!response.ok) {
        throw new Error('Payment service is currently unavailable. Please try again later.');
      }

      const payementData = {
        userId: user.uid,
        amount: amount,
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date(),
        paymentMethod: 'card'
      }

      paymentRef = await addDoc(collection(db, 'payments'), payementData);
      console.log('Payment Record created:', paymentRef);

      const { clientSecret } = await response.json();

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement)!,
        },
      });
      console.log(stripeError, paymentIntent);
      if (stripeError) {
        const errorMessage = stripeError.message || 'Payment failed. Please try again.';
        setCardError(errorMessage);
        onError(errorMessage, paymentRef.id);
      } else if (paymentIntent.status === 'succeeded') {
        // toast.success(`Payment of $${amount} was successful!`);

        onSuccess(paymentRef.id);
      } else {
        toast.error('Payment was not completed. Please try again.');
        onError('Payment was not completed. Please try again.', paymentRef.id);
      }
    } catch (error) {

      const errorMessage = error instanceof Error ? error.message : 'Payment processing failed';
      toast.error(errorMessage);
      setCardError(errorMessage);
      onError(errorMessage, paymentRef.id);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 bg-gray-100  rounded-lg my-4" >
        <CardElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: 'black',
                '::placeholder': {
                  color: '#94a3b8',
                },
              },
              invalid: {
                color: '#ef4444',
              },
            },
          }}
          onChange={(e) => {
            if (e.error) {
              setCardError(e.error.message);
            } else {
              setCardError(null);
            }
          }}
        />
      </div>
      
      {cardError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {cardError}
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full py-3 text-base"
      >
        {isProcessing ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
        ) : (
          `Pay $${amount}`
        )}
      </Button>
    </form>
  );
};

export const StripeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Elements stripe={stripePromise}>
    {children}
  </Elements>
);

export default PaymentForm;

// export default PaymentForm; 

// import { loadStripe } from '@stripe/stripe-js';
// import { Elements } from '@stripe/react-stripe-js';
// import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
// import { useState } from 'react';

// const stripePromise = loadStripe("pk_test_51QjT1PFy4dRJbMJIm7fEOv3tTQBsCRqLnFzwnOJMaelMSSpcfye6olUTBT35J98ssXae3SoQoBnfVi5BsYKVJhOF009zoUcPU4"!);

// // console.log('Environment Variables:', {
// //   stripeKey: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
// //   nodeEnv: process.env.NODE_ENV,
// // });

// console.log('Stripe Promise:', stripePromise);

// interface PaymentFormProps {
//   amount: number;
//   onSuccess: () => void;
//   onError: (error: string) => void;
// }

// const PaymentForm: React.FC<PaymentFormProps> = ({ amount, onSuccess, onError }) => {
//   const stripe = useStripe();
//   const elements = useElements();
//   const [isProcessing, setIsProcessing] = useState<boolean>(false);
//   const [errors, setErrors] = useState<{
//     cardNumber?: string;
//     cardExpiry?: string;
//     cardCvc?: string;
//     zipCode?: string;
//     general?: string;
//   }>({});
//   const [zipCode, setZipCode] = useState('');

//   const handleSubmit = async (event: React.FormEvent) => {
//     event.preventDefault();
    
//     if (!stripe || !elements) {
//       onError('Stripe is not initialized');
//       return;
//     }

//     const cardNumberElement = elements.getElement(CardNumberElement);
//     const cardExpiryElement = elements.getElement(CardExpiryElement);
//     const cardCvcElement = elements.getElement(CardCvcElement);

//     if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
//       onError('Card elements not found');
//       return;
//     }

//     if (!zipCode.trim()) {
//       setErrors(prev => ({ ...prev, zipCode: 'ZIP code is required' }));
//       return;
//     }

//     if (errors) {
//       console.log('Errors:', errors);
//       onError('Please fix the errors before submitting');
//       return;
//     }

//     setIsProcessing(true);
//     setErrors({});

//     try {
//       const response = await fetch('http://localhost:3001/api/payments/create-intent', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({ amount }),
//       });

//       if (!response.ok) {
//         throw new Error('Payment service is currently unavailable. Please try again later.');
//       }

//       const { clientSecret } = await response.json();

//       const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
//         payment_method: {
//           card: cardNumberElement,
//           billing_details: {
//             address: {
//               postal_code: zipCode,
//             },
//           },
//         },
//       });
      
//       console.log(stripeError, paymentIntent);
      
//       if (stripeError) {
//         const errorMessage = stripeError.message || 'Payment failed. Please try again.';
//         setErrors({ general: errorMessage });
//         onError(errorMessage);
//       } else if (paymentIntent.status === 'succeeded') {
//         onSuccess();
//       } else {
//         onError('Payment was not completed. Please try again.');
//       }
//     } catch (error) {
//       const errorMessage = error instanceof Error ? error.message : 'Payment processing failed';
//       setErrors({ general: errorMessage });
//       onError(errorMessage);
//     } finally {
//       setIsProcessing(false);
//     }
//   };

//   const cardElementStyle = {
//     style: {
//       base: {
//         fontSize: '16px',
//         color: '#1f2937',
//         fontFamily: '"Inter", sans-serif',
//         '::placeholder': {
//           color: '#94a3b8',
//         },
//         iconColor: '#64748b',
//       },
//       invalid: {
//         color: '#ef4444',
//         iconColor: '#ef4444',
//       },
//     },
//   };

//   const handleCardChange = (elementType: string) => (event: any) => {
//     if (event.error) {
//       setErrors(prev => ({ ...prev, [elementType]: event.error.message }));
//     } else {
//       setErrors(prev => {
//         const newErrors = { ...prev };
//         delete newErrors[elementType as keyof typeof errors];
//         return newErrors;
//       });
//     }
//   };

//   return (
//     <form onSubmit={handleSubmit} className="space-y-4">
//       <div className="bg-white rounded-lg shadow p-6 space-y-4">
//         <div className="space-y-2">
//           <label className="block text-sm font-medium text-gray-700">Card Number</label>
//           <div className="p-3 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
//             <CardNumberElement 
//               options={cardElementStyle}
//               onChange={handleCardChange('cardNumber')}
//             />
//           </div>
//           {errors.cardNumber && (
//             <p className="text-red-500 text-sm mt-1">{errors.cardNumber}</p>
//           )}
//         </div>
        
//         <div className="grid grid-cols-2 gap-4">
//           <div className="space-y-2">
//             <label className="block text-sm font-medium text-gray-700">Expiration Date</label>
//             <div className="p-3 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
//               <CardExpiryElement 
//                 options={cardElementStyle}
//                 onChange={handleCardChange('cardExpiry')}
//               />
//             </div>
//             {errors.cardExpiry && (
//               <p className="text-red-500 text-sm mt-1">{errors.cardExpiry}</p>
//             )}
//           </div>
          
//           <div className="space-y-2">
//             <label className="block text-sm font-medium text-gray-700">CVC</label>
//             <div className="p-3 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
//               <CardCvcElement 
//                 options={cardElementStyle}
//                 onChange={handleCardChange('cardCvc')}
//               />
//             </div>
//             {errors.cardCvc && (
//               <p className="text-red-500 text-sm mt-1">{errors.cardCvc}</p>
//             )}
//           </div>
//         </div>
        
//         <div className="space-y-2">
//           <label className="block text-sm font-medium text-gray-700">ZIP Code</label>
//           <input
//             type="text"
//             value={zipCode}
//             onChange={(e) => {
//               setZipCode(e.target.value);
//               if (e.target.value.trim()) {
//                 setErrors(prev => {
//                   const newErrors = { ...prev };
//                   delete newErrors.zipCode;
//                   return newErrors;
//                 });
//               }
//             }}
//             className="p-3 w-full   focus:ring-blue-500 focus:border-blue-500"
//             placeholder="ZIP / Postal Code"
//           />
//           {errors.zipCode && (
//             <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>
//           )}
//         </div>
//       </div>
      
//       {errors.general && (
//         <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
//           {errors.general}
//         </div>
//       )}

//       <button
//         type="submit"
//         disabled={!stripe || isProcessing }
//         className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium disabled:opacity-50 transition-colors"
//       >
//         {isProcessing ? (
//           <span className="flex items-center justify-center">
//             <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//               <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//               <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//             </svg>
//             Processing...
//           </span>
//         ) : (
//           `Pay $${amount}`
//         )}
//       </button>
//     </form>
//   );
// };

// export const StripeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
//   <Elements stripe={stripePromise}>
//     {children}
//   </Elements>
// );

